/**
 * DependencyAlertChips — header chips for OPEN dependency episodes.
 *
 * OPERATOR_ALERTING_PLAN_2026-09-15 Phase 1 (ruling D2: chip only, no toast;
 * D8: super_admin only — gated by the caller in Layout). An incident is a
 * STATE, not an event, so it gets a persistent chip that clicks through to
 * the Heartbeat card, where the episode already renders in plain language.
 *
 * Data: the same ['dashboard'] query the dashboard page polls, so React Query
 * dedupes when the dashboard is open; elsewhere this component owns a 30 s
 * poll. Layout also invalidates ['dashboard'] on assayerai:alerts events, so
 * a new episode appears within a second of the broadcast.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dashboardQueryOptions, getDashboard, type DependencyEpisode } from '../lib/api/dashboard.js';

function label(ep: DependencyEpisode): string {
  const cls = ep.failure_class.replace(new RegExp(`^${ep.dependency}_`), '').replace(/_/g, ' ');
  return `${ep.dependency}: ${cls}`;
}

export function DependencyAlertChips() {
  const { data } = useQuery({ ...dashboardQueryOptions(30_000), staleTime: 10_000 });
  const open = (data?.heartbeat?.episodes ?? []).filter(
    (e) => !e.closed_at && e.dependency !== 'unclassified',
  );
  if (open.length === 0) return null;

  const title = open
    .map((e) => `${label(e)} — ${e.failure_count} failure${e.failure_count === 1 ? '' : 's'}${e.sample_error ? `\n${e.sample_error}` : ''}`)
    .join('\n\n');

  return (
    <>
      {/* Named chips — desktop/tablet */}
      <div className="hidden md:flex items-center gap-2" role="status" aria-live="polite">
        {open.slice(0, 3).map((e) => (
          <Link
            key={e.episode_id}
            to="/dashboard#heartbeat"
            title={title}
            className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800 hover:bg-red-200"
          >
            <span className="h-2 w-2 rounded-full bg-current opacity-75" aria-hidden />
            {label(e)}
          </Link>
        ))}
        {open.length > 3 && (
          <Link to="/dashboard#heartbeat" className="text-xs font-semibold text-red-200 hover:underline" title={title}>
            +{open.length - 3} more
          </Link>
        )}
      </div>
      {/* Compact count — phones */}
      <Link
        to="/dashboard#heartbeat"
        className="md:hidden inline-flex items-center gap-1 flex-shrink-0 rounded-full bg-red-500/90 px-1.5 py-1"
        title={title}
        aria-label={`${open.length} open dependency incident${open.length === 1 ? '' : 's'}`}
      >
        <span className="h-2.5 w-2.5 rounded-full bg-white" />
        <span className="text-xs font-semibold text-white">{open.length}</span>
      </Link>
    </>
  );
}
